import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Calendar, AlertCircle, Clock, CheckCircle2, ClipboardList, Search, X, Route, MapPin, Phone, User } from 'lucide-react';
import { toast } from 'sonner';
import ServiceTasksPanel from '../components/ServiceTasksPanel';
import DayRoutePanel from '../components/DayRoutePanel';
import NextServiceNote from '../components/NextServiceNote';

const todayStr = () => new Date().toLocaleDateString('sv-SE');

const getStatusBadge = (status) => {
  const badges = {
    overdue: { text: 'Forfalt', className: 'badge-overdue' },
    due: { text: 'Denne uken', className: 'badge-due' },
    upcoming: { text: 'Kommende', className: 'badge-upcoming' },
    scheduled: { text: 'Planlagt', className: 'badge-scheduled' },
    paused: { text: 'Pauset', className: 'badge-no-cycle' },
    inactive: { text: 'Deaktivert', className: 'badge-no-cycle' },
    no_cycle: { text: 'Ingen syklus', className: 'badge-no-cycle' }
  };
  return badges[status] || badges.no_cycle;
};

const getStatusInfo = (status) => {
  const info = {
    overdue: {
      icon: AlertCircle,
      color: 'text-red-600',
      description: 'Disse kundene har forfalt rens og må gjøres snarest. Rensen vil automatisk flyttes til gjeldende uke til den er bekreftet.'
    },
    due: {
      icon: Calendar,
      color: 'text-emerald-600',
      description: 'Disse kundene skal renses denne uken'
    },
    upcoming: {
      icon: Clock,
      color: 'text-slate-600',
      description: 'Disse kundene skal renses neste uke'
    }
  };
  return info[status];
};

export const CleaningSchedule = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('status') || 'overdue';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [overdueCustomers, setOverdueCustomers] = useState([]);
  const [dueCustomers, setDueCustomers] = useState([]);
  const [upcomingCustomers, setUpcomingCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [performing, setPerforming] = useState(false);
  const [search, setSearch] = useState('');
  const [resolveNote, setResolveNote] = useState(true);
  const { user } = useContext(AuthContext);
  const isAdmin = ['admin', 'system_admin'].includes(user?.role);
  const [routeDate, setRouteDate] = useState(todayStr());
  const [routeUserId, setRouteUserId] = useState(user?.id);
  const [routes, setRoutes] = useState([]);
  const [users, setUsers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);

  useEffect(() => {
    fetchCustomersByStatus();
    axios.get(`${API}/customers`).then((r) => setAllCustomers((r.data || []).filter((c) => c.is_active !== false))).catch((err) => console.warn('[CleaningSchedule]', err));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/users`).then((r) => setUsers((r.data || []).filter((u) => u.is_active !== false && u.role !== 'lager'))).catch((err) => console.warn('[CleaningSchedule]', err));
  }, [isAdmin]);

  useEffect(() => {
    if (user?.id) setRouteUserId((prev) => prev || user.id);
  }, [user?.id]);

  const fetchRoutes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/day-routes`, { params: { date: routeDate } });
      setRoutes(res.data || []);
    } catch {
      toast.error('Kunne ikke hente dagsruter');
    }
  }, [routeDate]);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  const saveRoute = async (customerIds) => {
    try {
      const res = await axios.put(`${API}/day-routes`, { date: routeDate, user_id: routeUserId, customer_ids: customerIds });
      setRoutes(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke lagre rute');
    }
  };

  const myRouteIds = routes.find((r) => r.user_id === routeUserId)?.customer_ids || [];
  const assignedTo = routes.reduce((acc, r) => { (r.customer_ids || []).forEach((id) => { acc[id] = r.user_name; }); return acc; }, {});

  const toggleInRoute = (cid, e) => {
    e.stopPropagation();
    const inRoute = myRouteIds.includes(cid);
    saveRoute(inRoute ? myRouteIds.filter((x) => x !== cid) : [...myRouteIds, cid]);
    toast.success(inRoute ? 'Fjernet fra rute' : 'Lagt i rute');
  };

  const updateNote = (cid, note) => {
    const upd = (list) => list.map((it) => (it.customer.id === cid ? { ...it, customer: { ...it.customer, next_service_note: note } } : it));
    setOverdueCustomers(upd); setDueCustomers(upd); setUpcomingCustomers(upd);
  };

  const fetchCustomersByStatus = async () => {
    try {
      const [overdueRes, dueRes, upcomingRes] = await Promise.all([
        axios.get(`${API}/dashboard/customers-by-status?status=overdue`),
        axios.get(`${API}/dashboard/customers-by-status?status=due`),
        axios.get(`${API}/dashboard/customers-by-status?status=upcoming`)
      ]);

      setOverdueCustomers(overdueRes.data);
      setDueCustomers(dueRes.data);
      setUpcomingCustomers(upcomingRes.data);
    } catch (error) {
      toast.error('Kunne ikke hente rens planlegging');
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (customer, e) => {
    e.stopPropagation();
    setSelectedCustomer(customer);
    setResolveNote(true);
    setConfirmOpen(true);
  };

  const handlePerformCleaning = async () => {
    if (!selectedCustomer) return;
    setPerforming(true);
    try {
      await axios.post(`${API}/services`, {
        customer_id: selectedCustomer.id,
        service_type: 'Rens',
        notes: 'Utført fra rens planlegging',
        resolve_next_note: !!selectedCustomer.next_service_note && resolveNote
      });
      toast.success(`Rens utført for ${selectedCustomer.name}`);
      // Optimistic: remove from current list immediately
      const cid = selectedCustomer.id;
      setOverdueCustomers(prev => prev.filter(c => c.customer.id !== cid));
      setDueCustomers(prev => prev.filter(c => c.customer.id !== cid));
      setUpcomingCustomers(prev => prev.filter(c => c.customer.id !== cid));
      setConfirmOpen(false);
      setSelectedCustomer(null);
      fetchRoutes();
    } catch (error) {
      toast.error('Kunne ikke registrere rens');
    } finally {
      setPerforming(false);
    }
  };

  const getISOWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const renderCustomerList = (customers, status) => {
    if (customers.length === 0) {
      return (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <p className="text-slate-600">Ingen kunder i denne kategorien</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {customers.map((item) => {
          const badge = getStatusBadge(item.status);
          return (
            <Card
              key={item.customer.id}
              data-testid={`customer-${status}-${item.customer.id}`}
              className="customer-card cursor-pointer"
              onClick={() => navigate(`/customers/${item.customer.id}`)}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex justify-between items-start mb-2 gap-2">
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {item.customer.name}
                  </h3>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {assignedTo[item.customer.id] && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-brand-100 text-brand-700 inline-flex items-center gap-1" data-testid={`route-badge-${item.customer.id}`}>
                        <Route className="h-3 w-3" />{assignedTo[item.customer.id]}
                      </span>
                    )}
                    <span className={`badge ${badge.className}`}>
                      {badge.text}
                    </span>
                  </div>
                </div>
                <div className="mb-2">
                  <NextServiceNote customerId={item.customer.id} note={item.customer.next_service_note} onSaved={(n) => updateNote(item.customer.id, n)} />
                </div>
                <div className="space-y-1.5 text-sm text-slate-600">
                  {(item.customer.address || item.customer.region) && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([item.customer.address, item.customer.postal_code].filter(Boolean).join(' '))}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-start gap-1.5 hover:text-brand-700"
                      data-testid={`cleaning-address-${item.customer.id}`}
                    >
                      <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
                      <span>
                        {item.customer.postal_code && !(item.customer.address || '').includes(item.customer.postal_code) ? `${item.customer.address}, ${item.customer.postal_code}` : item.customer.address}
                        {item.customer.region && <span className="text-slate-400" data-testid={`cleaning-region-${item.customer.id}`}> · {item.customer.region}</span>}
                      </span>
                    </a>
                  )}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    {item.customer.contact_person && (
                      <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4 text-slate-400" />{item.customer.contact_person}</span>
                    )}
                    {item.customer.phone && (
                      <a href={`tel:${item.customer.phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 font-medium text-brand-700 py-1" data-testid={`cleaning-phone-${item.customer.id}`}>
                        <Phone className="h-4 w-4" />{item.customer.phone}
                      </a>
                    )}
                  </div>
                  {(item.customer.door_code || item.customer.gate_code || item.customer.alarm_code) && (
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`cleaning-codes-${item.customer.id}`}>
                      {item.customer.door_code && (
                        <span className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700">Dør: <strong>{item.customer.door_code}</strong></span>
                      )}
                      {item.customer.gate_code && (
                        <span className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700">Port: <strong>{item.customer.gate_code}</strong></span>
                      )}
                      {item.customer.alarm_code && (
                        <span className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700">Alarm: <strong>{item.customer.alarm_code}</strong></span>
                      )}
                    </div>
                  )}
                  {item.customer.access_info && (
                    <div className="flex items-start">
                      <span className="font-medium mr-2 shrink-0">Tilgang:</span>
                      <span className="whitespace-pre-wrap line-clamp-2">{item.customer.access_info}</span>
                    </div>
                  )}
                  {item.cleaning_cycle && (
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500 pt-0.5" data-testid={`cleaning-cycle-${item.customer.id}`}>
                      <span>Sist renset: <strong className="text-slate-700">uke {item.cleaning_cycle.last_cleaned_week}/{item.cleaning_cycle.last_cleaned_year}</strong></span>
                      <span>
                        {status === 'overdue' ? 'Skulle vært: ' : 'Neste: '}
                        <strong className={status === 'overdue' ? 'text-red-700' : 'text-slate-700'}>uke {item.cleaning_cycle.next_due_week}/{item.cleaning_cycle.next_due_year}</strong>
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:justify-end">
                  <Button
                    data-testid={`toggle-route-${item.customer.id}`}
                    size="sm"
                    variant="outline"
                    onClick={(e) => toggleInRoute(item.customer.id, e)}
                    className={myRouteIds.includes(item.customer.id) ? 'border-brand-300 text-brand-700 bg-brand-50' : ''}
                  >
                    <Route className="h-4 w-4 mr-1.5" />
                    {myRouteIds.includes(item.customer.id) ? 'I ruten' : 'Legg i rute'}
                  </Button>
                  <Button
                    data-testid={`perform-cleaning-${item.customer.id}`}
                    size="sm"
                    onClick={(e) => openConfirm(item.customer, e)}
                    className="bg-brand-600 hover:bg-brand-700 text-white"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1.5" />
                    Utfør rens
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Laster...</div>
      </div>
    );
  }

  const filterList = (list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => {
      const c = item.customer || {};
      return [c.name, c.contact_person, c.phone, c.address, c.region, c.door_code, c.gate_code, c.alarm_code]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
  };

  const overdueList = filterList(overdueCustomers);
  const dueList = filterList(dueCustomers);
  const upcomingList = filterList(upcomingCustomers);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Rens Planlegging
        </h1>
        <p className="text-slate-600">Oversikt over alle rens sykluser</p>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          data-testid="cleaning-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Søk etter kunde, område, kontakt, telefon, adresse eller kode..."
          className="pl-9 pr-9"
        />
        {search && (
          <button
            type="button"
            data-testid="cleaning-search-clear"
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            aria-label="Tøm søk"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex flex-wrap w-full h-auto gap-1 justify-start">
          <TabsTrigger value="overdue" data-testid="overdue-tab" className="flex-1 min-w-[120px]">
            Forfalt
            {overdueList.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                {overdueList.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="due" data-testid="due-tab" className="flex-1 min-w-[120px]">
            Denne uken
            {dueList.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full">
                {dueList.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="upcoming-tab" className="flex-1 min-w-[120px]">
            Kommende
            {upcomingList.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-slate-100 text-slate-700 rounded-full">
                {upcomingList.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="route" data-testid="route-tab" className="flex-1 min-w-[120px]">
            <Route className="h-4 w-4 mr-1.5" />
            Dagsrute
            {myRouteIds.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-brand-100 text-brand-700 rounded-full">{myRouteIds.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="other-tasks" data-testid="other-tasks-tab" className="flex-1 min-w-[120px]">
            <ClipboardList className="h-4 w-4 mr-1.5" />
            Andre oppdrag
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overdue">
          <div className="mb-4">
            <Card className="bg-red-50 border-red-200">
              <CardContent className="py-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-900">Forfalt rens</p>
                    <p className="text-sm text-red-700 mt-1">
                      Disse kundene har forfalt rens og må gjøres snarest. Rensen vil automatisk flyttes til gjeldende uke til den er bekreftet.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {renderCustomerList(overdueList, 'overdue')}
        </TabsContent>

        <TabsContent value="due">
          <div className="mb-4">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="py-4">
                <div className="flex items-start">
                  <Calendar className="h-5 w-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-900">Denne uken</p>
                    <p className="text-sm text-emerald-800 mt-1">
                      Disse kundene skal renses denne uken
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {renderCustomerList(dueList, 'due')}
        </TabsContent>

        <TabsContent value="upcoming">
          <div className="mb-4">
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="py-4">
                <div className="flex items-start">
                  <Clock className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">Kommende</p>
                    <p className="text-sm text-slate-700 mt-1">
                      Disse kundene skal renses neste uke
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {renderCustomerList(upcomingList, 'upcoming')}
        </TabsContent>

        <TabsContent value="route">
          <DayRoutePanel
            routes={routes}
            routeDate={routeDate}
            setRouteDate={setRouteDate}
            routeUserId={routeUserId}
            setRouteUserId={setRouteUserId}
            users={users}
            isAdmin={isAdmin}
            currentUser={user}
            customers={allCustomers}
            onSave={saveRoute}
            onPerform={openConfirm}
          />
        </TabsContent>

        <TabsContent value="other-tasks">
          <div className="mb-4">
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="py-4">
                <div className="flex items-start">
                  <ClipboardList className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">Andre oppdrag</p>
                    <p className="text-sm text-slate-700 mt-1">
                      Akutte og vanlige serviceoppdrag med dato. Opprett, fullfør eller slett oppdrag direkte herfra.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <ServiceTasksPanel />
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmOpen} onOpenChange={(open) => { if (!performing) setConfirmOpen(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bekreft rens</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil registrere rens for <strong>{selectedCustomer?.name}</strong>? 
              Renssyklusen vil bli oppdatert automatisk.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {selectedCustomer?.next_service_note && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-300 space-y-2" data-testid="confirm-next-note">
              <p className="text-sm text-amber-900"><strong>Til neste service:</strong> {selectedCustomer.next_service_note}</p>
              <label className="flex items-center gap-2 text-sm text-amber-900 cursor-pointer">
                <input type="checkbox" checked={resolveNote} onChange={(e) => setResolveNote(e.target.checked)} className="h-4 w-4" data-testid="confirm-resolve-note" />
                Merknaden er utført (fjernes og lagres i historikken)
              </label>
            </div>
          )}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
            <Button
              variant="outline"
              disabled={performing}
              onClick={() => setConfirmOpen(false)}
            >
              Avbryt
            </Button>
            <Button
              data-testid="confirm-perform-cleaning"
              onClick={handlePerformCleaning}
              disabled={performing}
              className="bg-brand-600 hover:bg-brand-700 text-white"
            >
              {performing ? 'Registrerer...' : 'Bekreft rens'}
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CleaningSchedule;
